import { useState, useEffect, useMemo } from 'react';
import { NotesEditor } from '@/components/editor/NotesEditor';
import { Flex, Icon, Checkbox, Tooltip, Box, Button } from '@chakra-ui/react';
import { FaArrowCircleUp } from 'react-icons/fa';
import { parseNoteToFormat } from './notesHelpers';
import { NotesAccessMenu } from './NotesAccessmenu';
import { KText } from '@/ui-library/components/text';
import { KTag } from '@/ui-library/components';
import { InfoCircle } from '@kula-ai/icons/dual-tone/InfoCircle';
import { MdArrowUpward } from 'react-icons/md';

export const NewNote = ({
  handleNoteAdd = (payload) => {},
  scrollToBottom = () => {},
  type,
  reference,
  editorHeight = '120px',
}) => {
  const [note, setNote] = useState('');
  const [shared, setShared] = useState(false);
  const [isEnterPressed, setEnterPressed] = useState(false);
  const [access, setAccess] = useState('public');

  useEffect(() => {
    if (!isEnterPressed) return;
    handleSubmit();
  }, [isEnterPressed]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    const { note: noteInTextArea, taggedUsers } = parseNoteToFormat(note);
    if (noteInTextArea?.trim() === '') return;
    const payload = {
      body: noteInTextArea,
      private: access === 'private',
      tagged_user_ids: taggedUsers,
      shared: shared && access !== 'private',
    };
    handleNoteAdd({ payload, scrollToBottom });
  };

  const getActionContainerStyles = useMemo(() => {
    if (reference === 'application_revamp') {
      return {
        justifyContent: 'space-between',
        width: '100%',
        alignItems: 'center',
        padding: '0 10px',
      };
    }
    return {};
  }, [reference]);

  return (
    <Flex
      w="100%"
      border={type === 'quick_app_review' ? '1px solid' : 'unset'}
      borderRadius={type === 'quick_app_review' ? 'sm' : 'unset'}
      borderTop="1px solid"
      borderColor="border-color"
      flexDir="column"
    >
      <Flex w="100%" flexDir="column" p="10px" pb="0px">
        <NotesEditor
          onUpdate={(html) => {
            setNote(html);
            setEnterPressed(false);
          }}
          onEnter={() => setEnterPressed((prev) => !prev)}
          isEnterPressed={isEnterPressed}
          height={editorHeight}
        />
      </Flex>
      <Flex
        justifyContent="space-between"
        gap="10px"
        mb={type === 'quick_app_review' ? '12px' : '20px'}
      >
        {reference !== 'application_revamp' && (
          <Flex>
            {type === 'interview' && access !== 'private' && (
              <Flex
                ml="10px"
                alignItems="center"
                gap="6px"
                cursor={access === 'private' && 'not-allowed'}
              >
                <Checkbox
                  isChecked={shared && access !== 'private'}
                  onChange={(e) => setShared(e.target.checked)}
                  disabled={access === 'private'}
                />
                <KText fontSize="sm" opacity={access === 'private' ? 0.5 : 1}>
                  Share with other interviewers
                </KText>
              </Flex>
            )}
            <Flex ml="10px" minH="32px">
              {access === 'private' && type !== 'quick_app_review' && (
                <KTag
                  value={
                    <KText display="inline">
                      <KText
                        display="inline"
                        size="sm-medium"
                        color="secondary-900"
                      >
                        Note:{' '}
                      </KText>
                      Users who are tagged or have private note access
                      permission can see this message
                    </KText>
                  }
                />
              )}
            </Flex>
          </Flex>
        )}

        <Flex alignItems="center" {...getActionContainerStyles}>
          <Flex alignItems="center">
            <NotesAccessMenu selected={access} setSelected={setAccess} />
            {type === 'quick_app_review' && access === 'private' && (
							<Tooltip
								label="Users who are tagged or have private note access permission can see this message"
								hasArrow
								placement="top"
							>
								<Box display="inline-flex" ml="3px">
									<Icon as={InfoCircle} w="14px" h="14px" />
								</Box>
							</Tooltip>
            )}
          </Flex>
          <Flex>
            {reference === 'application_revamp' && (
              <Button
                onClick={() => setEnterPressed(true)}
                bg="primary-600"
                minWidth="24px"
                minHeight="24px"
                padding="0 !important"
                width="24px"
                height="24px"
                borderRadius="50px"
              >
                <Icon
                  as={MdArrowUpward}
                  w="16px"
                  h="16px"
                  data-test-id="send-notes-button"
                />
              </Button>
            )}
            {reference !== 'application_revamp' && (
              <Icon
                as={FaArrowCircleUp}
                w="22px"
                h="22px"
                color="ats-primary"
                mr={type === 'quick_app_review' ? '12px' : '30px'}
                ml="12px"
                cursor="pointer"
                onClick={() => setEnterPressed(true)}
                data-test-id="send-notes-button"
              />
            )}
          </Flex>
        </Flex>
      </Flex>
    </Flex>
  );
};
